// @ts-strict

import { Injectable } from '@angular/core';
import { LMErrorUtilService } from '@app/services';
import { LMDrillAroundNode } from '@components/drillaround/lm-drill-around-wc';
import { TreeNodeBadge } from 'ids-enterprise-wc/components/ids-tree/ids-tree-node';

@Injectable()
export class LMDrillNavigationService {

  private rootDataView!: LMDataView ;
  private rootDrillActions!: Array<LMDrillAction>;
  private uniqueRecordId = 1;
  private recordNodeMap = new Map<string, string>();

  constructor(
    private lmErrorUtilService: LMErrorUtilService
  ) {}

  setRootDrillRequest(rootDataView: LMDataView, rootDrillActions: Array<LMDrillAction>) {
    this.rootDataView = rootDataView;
    this.rootDrillActions = rootDrillActions;
  }

  public getRootTreeNodes(): LMDrillAroundNode[] {
    if (!this.rootDrillActions || !this.rootDataView) {
      this.lmErrorUtilService.handleError('rootDrillRequest is null');
      return [] as LMDrillAroundNode[];
    }
    const title = this.rootDataView.representativeText ? this.rootDataView.representativeText : this.rootDataView.busClassName;
    const node: LMDrillAroundNode = {
      id: '_root_',
      dataView: this.rootDataView,
      expanded: false,
      badge: {
        text: title,
        color: 'info'
      },
      lmKey: this.rootDataView.resourceId,
      lmType: 'record'
    };

    if (this.rootDataView.resourceId && node.id) {
      this.recordNodeMap.set(this.rootDataView.resourceId, node.id);
    }

    return [ node ];
  }

  public getDrillRecordNodeId(resourceId: string | undefined): string | undefined {
    return resourceId ? this.recordNodeMap.get(resourceId) : undefined;
  }

  public buildDrillRecordNode(parentNode: LMDrillAroundNode, item: LMDrillAroundDetail): LMDrillAroundNode {
    const title = item.dataView?.representativeText ? item.dataView.representativeText : item.dataView?.busClassName;
    const node: LMDrillAroundNode = {
      id:       parentNode.id + '-' + this.getRecordUniqueId(),
      lmKey:    item.dataView?.resourceId,
      lmType:   'record',
      parent:   parentNode.id,
      dataView: item.dataView,
      lmParent: parentNode,
      expanded: true,
      badge: {
        text: title,
        color: 'info'
      }
    };

    if (item.dataView?.resourceId && node.id) {
      this.recordNodeMap.set(item.dataView?.resourceId, node.id);
    }

    if (item.dataView && item.drillActions) {
      const drillAroundNodes = this.buildDrillAroundNodes(item.dataView, item.drillActions, node);
      node.children = drillAroundNodes; // add drillpoint nodes for the record node, idsTree.setNodes() handles this well.
      node.lmParent = parentNode;
      node.lmChildren = drillAroundNodes;
    }

    return node;
  }

  public buildDrillPanelNode(parentNode: LMDrillAroundNode, item: LMDrillAroundDetail): LMDrillAroundNode {

    const node: LMDrillAroundNode = {
      id:           this.getRecordUniqueId(),
      lmKey:        item.panelIndex,
      lmType:       'panel',
      parent:       parentNode ? parentNode.id : undefined,
      text:         item.panel,
      lmParent:     parentNode,
    };

    return node;
  }

  public buildDrillAroundNodes(dataView: LMDataView, drillActions: Array<LMDrillAction>, parentNode: LMDrillAroundNode): Array<LMDrillAroundNode> {
    const nodes: Array<LMDrillAroundNode> = [];

    if (drillActions) {
      drillActions.forEach(drill => {
        nodes.push(this.buildDrillActionNode(dataView, drill, parentNode));
      });
    }

    return nodes;
  }

  private buildDrillActionNode(dataView: LMDataView, drillAction: LMDrillAction, parentNode: LMDrillAroundNode): LMDrillAroundNode {

    const node: LMDrillAroundNode = {
      id:           parentNode.id + '-' + drillAction.drillId,
      lmKey:        drillAction.drillId,
      lmType:       'drill',
      text:         drillAction.drillName,
      drillAction:  drillAction,
      dataView:     dataView,
      lmParent:     parentNode,
      // children:     undefined // drillAction.drillType === 'LIST' ? [] : undefined
    };

    return node;
  }

  private getRecordUniqueId() {

    const id = 'n' + this.uniqueRecordId;
    this.uniqueRecordId++;

    return id;
  }
}
